import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EMPLOYMENT_TYPES, SALARY_TYPES } from "./constants";

export const EmployeeFormDialog = ({
  open,
  onOpenChange,
  selectedEmployee,
  formData,
  setFormData,
  onSave,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{selectedEmployee ? "Edit Employee" : "Add New Employee"}</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Employee Number *</Label>
            <Input 
              value={formData.employee_number}
              onChange={(e) => setFormData({...formData, employee_number: e.target.value})}
              placeholder="EMP001"
              data-testid="employee-number-input"
            />
          </div>
          <div>
            <Label>ID Number</Label>
            <Input 
              value={formData.id_number}
              onChange={(e) => setFormData({...formData, id_number: e.target.value})}
              placeholder="SA ID Number"
              data-testid="employee-id-number-input"
            />
          </div>
          <div>
            <Label>First Name *</Label>
            <Input 
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              data-testid="employee-first-name-input"
            />
          </div>
          <div>
            <Label>Last Name *</Label>
            <Input 
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              data-testid="employee-last-name-input"
            />
          </div>
          <div>
            <Label>Date of Birth</Label>
            <Input 
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
              data-testid="employee-dob-input"
            />
          </div>
          <div>
            <Label>Contact Number</Label>
            <Input 
              value={formData.contact_number}
              onChange={(e) => setFormData({...formData, contact_number: e.target.value})}
              data-testid="employee-contact-input"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input 
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              data-testid="employee-email-input"
            />
          </div>
          <div>
            <Label>Position *</Label>
            <Input 
              value={formData.position}
              onChange={(e) => setFormData({...formData, position: e.target.value})}
              placeholder="e.g., Sales Assistant"
              data-testid="employee-position-input"
            />
          </div>
          <div>
            <Label>Department</Label>
            <Input 
              value={formData.department}
              onChange={(e) => setFormData({...formData, department: e.target.value})}
              data-testid="employee-department-input"
            />
          </div>
          <div>
            <Label>Start Date *</Label>
            <Input 
              type="date"
              value={formData.start_date}
              onChange={(e) => setFormData({...formData, start_date: e.target.value})}
              data-testid="employee-start-date-input"
            />
          </div>
          <div>
            <Label>Employment Type</Label>
            <Select value={formData.employment_type} onValueChange={(v) => setFormData({...formData, employment_type: v})}>
              <SelectTrigger data-testid="employee-employment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...EMPLOYMENT_TYPES].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map(t => (
                  <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Salary Type</Label>
            <Select value={formData.salary_type} onValueChange={(v) => setFormData({...formData, salary_type: v})}>
              <SelectTrigger data-testid="employee-salary-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...SALARY_TYPES].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map(t => (
                  <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{formData.salary_type === 'monthly' ? 'Monthly Salary' : formData.salary_type === 'casual' ? 'Default Hourly Rate (R) — used as a hint on the Casual Wages page' : 'Hourly Rate'} (R)</Label>
            <Input 
              type="number"
              value={formData.basic_salary}
              onChange={(e) => setFormData({...formData, basic_salary: parseFloat(e.target.value) || 0})}
              data-testid="employee-salary-input"
            />
          </div>
          <div>
            <Label>Tax Number</Label>
            <Input 
              value={formData.tax_number}
              onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
              data-testid="employee-tax-number-input"
            />
          </div>
          
          <div className="col-span-2">
            <Label>Address</Label>
            <Input 
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              data-testid="employee-address-input"
            />
          </div>
          
          <div className="col-span-2 border-t pt-4 mt-2">
            <h4 className="font-medium mb-2">Bank Details</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Input 
                  value={formData.bank_name}
                  onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                  data-testid="employee-bank-name-input"
                />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input 
                  value={formData.bank_account_number}
                  onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                  data-testid="employee-bank-account-input"
                />
              </div>
              <div>
                <Label>Branch Code</Label>
                <Input 
                  value={formData.bank_branch_code}
                  onChange={(e) => setFormData({...formData, bank_branch_code: e.target.value})}
                  data-testid="employee-bank-branch-input"
                />
              </div>
            </div>
          </div>

          <div className="col-span-2 border-t pt-4 mt-2">
            <h4 className="font-medium mb-2">Clock In/Out PIN</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>4-Digit PIN (for clock in/out)</Label>
                <Input 
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={formData.pin || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setFormData({...formData, pin: val});
                  }}
                  placeholder="Enter 4-digit PIN"
                  data-testid="employee-pin-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Employee will use this PIN to clock in/out at the login screen
                </p>
              </div>
            </div>
          </div>

          <div className="col-span-2 border-t pt-4 mt-2">
            <h4 className="font-medium mb-2">Leave Entitlement (Days per year)</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Annual Leave</Label>
                <Input 
                  type="number"
                  value={formData.annual_leave_days}
                  onChange={(e) => setFormData({...formData, annual_leave_days: parseFloat(e.target.value) || 0})}
                  data-testid="employee-annual-leave-input"
                />
              </div>
              <div>
                <Label>Sick Leave (3-year cycle)</Label>
                <Input 
                  type="number"
                  value={formData.sick_leave_days}
                  onChange={(e) => setFormData({...formData, sick_leave_days: parseFloat(e.target.value) || 0})}
                  data-testid="employee-sick-leave-input"
                />
              </div>
              <div>
                <Label>Family Responsibility</Label>
                <Input 
                  type="number"
                  value={formData.family_leave_days}
                  onChange={(e) => setFormData({...formData, family_leave_days: parseFloat(e.target.value) || 0})}
                  data-testid="employee-family-leave-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Salesperson / commission attribution */}
        <div className="border-t pt-4 mt-2">
          <div className="text-sm font-semibold mb-2 text-amber-700">Sales attribution (for commission tracking)</div>
          <div className="grid grid-cols-2 gap-4 items-end">
            <div className="flex items-center gap-3 p-3 border rounded">
              <input
                id="is-salesperson"
                type="checkbox"
                checked={!!formData.is_salesperson}
                onChange={(e) => setFormData({ ...formData, is_salesperson: e.target.checked })}
                className="h-4 w-4"
                data-testid="employee-is-salesperson"
              />
              <label htmlFor="is-salesperson" className="text-sm cursor-pointer flex-1">
                <div className="font-medium">Salesperson</div>
                <div className="text-xs text-gray-500">Eligible to be credited at the till and earn commission</div>
              </label>
            </div>
            <div>
              <Label>Marketing # (typed at the till)</Label>
              <Input
                value={formData.marketing_number || ""}
                onChange={(e) => setFormData({ ...formData, marketing_number: e.target.value.replace(/[^0-9A-Za-z]/g, "").slice(0, 6) })}
                placeholder="e.g. 07"
                className="font-mono"
                data-testid="employee-marketing-number"
                disabled={!formData.is_salesperson}
              />
              <p className="text-xs text-gray-500 mt-1">Short unique code (2–6 chars). Must be unique across active salespeople.</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} className="bg-green-600 hover:bg-green-700" data-testid="save-employee-btn">
            {selectedEmployee ? "Update" : "Create"} Employee
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
